// src/components/property/ImageLightbox.tsx
/**
 * ImageLightbox Component
 *
 * Full-screen lightbox for viewing property images with navigation.
 * Supports keyboard navigation (arrows, escape) and constrains
 * image height to viewport.
 */

import { useState, useEffect, useCallback, memo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Dialog, DialogOverlay, DialogPortal } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import type { IImageLightboxProps } from '@/@types/property';
import type { Id } from '../../../convex/_generated/dataModel';

/**
 * Helper component to load and display a single image
 */
function LightboxImage({ storageId }: { storageId: Id<'_storage'> }) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  if (!imageUrl) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="h-8 w-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <img src={imageUrl} alt="Property image" className="max-h-[90vh] max-w-full object-contain" />
  );
}

/**
 * ImageLightbox - Full-screen image viewer with navigation
 */
function ImageLightbox({ imageIds, initialIndex, isOpen, onClose }: IImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(() => initialIndex);

  // Track the last prop snapshot we synced currentIndex from so we can detect
  // open/initialIndex changes during render (React's derived-state pattern — no effect needed).
  const [syncedProps, setSyncedProps] = useState(() => ({ isOpen, initialIndex }));
  if (isOpen && (syncedProps.isOpen !== isOpen || syncedProps.initialIndex !== initialIndex)) {
    setSyncedProps({ isOpen, initialIndex });
    if (currentIndex !== initialIndex) {
      setCurrentIndex(initialIndex);
    }
  }

  const hasMultipleImages = imageIds.length > 1;

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : imageIds.length - 1));
  }, [imageIds.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < imageIds.length - 1 ? prev + 1 : 0));
  }, [imageIds.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, onClose]);

  const currentImageId = imageIds[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Navigation - Previous */}
          {hasMultipleImages && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
          )}

          {/* Image container */}
          <div className="flex items-center justify-center px-16">
            {currentImageId && <LightboxImage storageId={currentImageId} />}
          </div>

          {/* Navigation - Next */}
          {hasMultipleImages && (
            <button
              onClick={goToNext}
              className="absolute right-4 z-50 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
              aria-label="Next image"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          )}

          {/* Image counter */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-black/50 text-white text-sm">
              {currentIndex + 1} of {imageIds.length}
            </div>
          )}
        </div>
      </DialogPortal>
    </Dialog>
  );
}

export const ImageLightboxMemo = memo(ImageLightbox);
export { ImageLightbox };
